/**
 * moveLog → 人間が読める表示テキストへの変換ユーティリティ
 *
 * ReplayViewer でターンごとの操作ログを表示するために使用。
 * 表示不要な Move（proceedAutoPhase 等）は除外する。
 */

import type { GameLogEntry } from '../types';

export interface FormattedLogEntry {
  icon: string;
  text: string;
}

/** 非表示にする Move 名 */
const HIDDEN_MOVES = new Set([
  'proceedAutoPhase',
  'triggerDiceRollForProduction',
]);

/**
 * 指定ターンの moveLog エントリを人間が読めるテキストに変換する。
 * turn=0 は初期セットアップ（通常表示なし）。
 */
export function formatMoveLog(
  moveLog: unknown[],
  turn: number,
): FormattedLogEntry[] {
  const entries = (moveLog as GameLogEntry[]).filter(
    (entry) => entry.turn === turn,
  );

  const result: FormattedLogEntry[] = [];

  for (const entry of entries) {
    // AutoPhaseLogEntry
    if ('type' in entry && entry.type === 'autoPhase') {
      const formatted = formatAutoPhase(entry);
      if (formatted) result.push(formatted);
      continue;
    }

    // MoveLogEntry — skip hidden moves
    if ('move' in entry && HIDDEN_MOVES.has(entry.move)) {
      continue;
    }

    if ('move' in entry) {
      const formatted = formatMove(entry.move, entry.args, entry.phase);
      if (formatted) result.push(formatted);
    }
  }

  return result;
}

function formatAutoPhase(entry: { phase: string; randomResults: Record<string, unknown> }): FormattedLogEntry | null {
  const results = entry.randomResults;

  // Dice roll (refillGoods phase) — field name is "diceRolls" or "dice"
  const dice = results.diceRolls ?? results.dice;
  if (dice) {
    // Detroit: diceRolls is { light: [n,n], dark: [n,n] }, others: [n, n]
    const diceText = Array.isArray(dice)
      ? `Dice: ${(dice as number[]).join(', ')}`
      : `Light: ${(dice as any).light.join(',')} Dark: ${(dice as any).dark.join(',')}`;

    const placements = results.placements;
    if (Array.isArray(placements) && placements.length > 0) {
      const placementText = placements
        .map((p: any) => `${p.color}→${p.cityName || `(${p.hex?.q},${p.hex?.r})`}`)
        .join(', ');
      return { icon: '🎲', text: `${diceText} → ${placementText}` };
    }
    return { icon: '🎲', text: diceText };
  }

  // Production (goods drawn from bag)
  if (results.drawnGoods) {
    const goods = results.drawnGoods as string[];
    return { icon: '🎲', text: `Drew ${goods.join(', ')} from bag` };
  }

  return null;
}

function formatMove(move: string, args: unknown, _phase: string): FormattedLogEntry | null {
  switch (move) {
    case 'issueShares': {
      const a = args as { issueCount?: number; count?: number } | undefined;
      const n = a?.issueCount ?? a?.count ?? 0;
      return { icon: '📈', text: n === 0 ? 'No shares issued' : `Issued ${n} share(s)` };
    }
    case 'selectAction': {
      const a = args as { action?: string } | undefined;
      return { icon: '🎯', text: `Selected ${a?.action ?? '?'}` };
    }
    case 'buildTrack': {
      const a = args as { hexCoordinate?: { q: number; r: number }; coordinate?: { q: number; r: number }; tileType?: string } | undefined;
      const coord = a?.hexCoordinate ?? a?.coordinate;
      const tile = a?.tileType ?? '?';
      return {
        icon: '🔧',
        text: coord ? `Built ${tile} at (${coord.q},${coord.r})` : `Built ${tile}`,
      };
    }
    case 'cancelAllBuildTrack':
      return { icon: '↩️', text: 'Cancelled all tracks' };
    case 'passBuildTrack':
      return { icon: '✅', text: 'Finished building' };
    case 'moveGoods': {
      const a = args as { goodsColor?: string; color?: string; fromCityId?: string; from?: string; toCityId?: string; to?: string } | undefined;
      const color = a?.goodsColor ?? a?.color ?? '?';
      const from = a?.fromCityId ?? a?.from ?? '?';
      const to = a?.toCityId ?? a?.to ?? '?';
      return { icon: '📦', text: `Delivered ${color} from ${from} to ${to}` };
    }
    case 'passMoveGoods':
      return { icon: '✅', text: 'Finished deliveries' };
    case 'upgradeLoco':
      return { icon: '🚂', text: 'Upgraded locomotive' };
    case 'executeProduction': {
      const a = args as { placements?: Array<{ color?: string }>; color?: string } | undefined;
      if (a?.placements && a.placements.length > 0) {
        const colors = a.placements.map(p => p.color).filter(Boolean).join(', ');
        return { icon: '📦', text: `Placed ${colors} on display` };
      }
      return { icon: '📦', text: a?.color ? `Placed ${a.color} on display` : 'Placed goods on display' };
    }
    default:
      return { icon: '▶️', text: move };
  }
}
